import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FolderDateFilter, FolderSignedFilter } from '../types'

export type ExplorerTab = 'folders' | 'trash'
export type ExplorerViewMode = 'cards' | 'list'
export type ExplorerItemType = 'folder' | 'document' | 'template'

export interface ExplorerSelectionItem {
  type: ExplorerItemType
  id: string
}

export interface ExplorerBreadcrumbItem {
  id: string
  name: string
  parentId?: string | null
}

interface OpenFolderPayload extends ExplorerBreadcrumbItem {}

interface GoToFolderPayload extends ExplorerBreadcrumbItem {
  breadcrumb: ExplorerBreadcrumbItem[]
}

export interface DocumentExplorerState {
  activeTab: ExplorerTab
  activeFolderId: string | null
  activeFolderName: string | null
  breadcrumb: ExplorerBreadcrumbItem[]
  searchText: string
  page: number
  dateFilter: FolderDateFilter
  signedFilter: 'all' | FolderSignedFilter
  viewMode: ExplorerViewMode
  selectedItems: ExplorerSelectionItem[]
  lastSelectedItem: ExplorerSelectionItem | null
}

export const EXPLORER_VIEW_MODE_STORAGE_KEY = 'firma-digital-soe-explorer-view-mode'

export const getStoredExplorerViewMode = (): ExplorerViewMode => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(EXPLORER_VIEW_MODE_STORAGE_KEY)
    if (stored === 'cards' || stored === 'list') {
      return stored
    }
  }
  return 'cards'
}

const initialState: DocumentExplorerState = {
  activeTab: 'folders',
  activeFolderId: null,
  activeFolderName: null,
  breadcrumb: [],
  searchText: '',
  page: 1,
  dateFilter: 'all',
  signedFilter: 'all',
  viewMode: getStoredExplorerViewMode(),
  selectedItems: [],
  lastSelectedItem: null,
}

export const isExplorerItemSelected = (
  selectedItems: ExplorerSelectionItem[],
  item: ExplorerSelectionItem
) => selectedItems.some((selected) => selected.type === item.type && selected.id === item.id)

export const documentExplorerSlice = createSlice({
  name: 'documentExplorer',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<ExplorerTab>) => {
      state.activeTab = action.payload
      state.selectedItems = []
      state.lastSelectedItem = null
    },
    openFolder: (state, action: PayloadAction<OpenFolderPayload>) => {
      const { id, name, parentId } = action.payload
      state.activeFolderId = id
      state.activeFolderName = name
      state.selectedItems = []
      state.lastSelectedItem = null

      const existingIndex = state.breadcrumb.findIndex((folder) => folder.id === id)
      if (existingIndex >= 0) {
        state.breadcrumb = state.breadcrumb.slice(0, existingIndex + 1)
        return
      }

      state.breadcrumb.push({ id, name, parentId })
    },
    goToFolder: (state, action: PayloadAction<GoToFolderPayload>) => {
      const { id, name, breadcrumb } = action.payload
      state.activeFolderId = id
      state.activeFolderName = name
      state.breadcrumb = breadcrumb
      state.selectedItems = []
      state.lastSelectedItem = null
    },
    goBackToRoot: (state) => {
      state.activeFolderId = null
      state.activeFolderName = null
      state.breadcrumb = []
      state.selectedItems = []
      state.lastSelectedItem = null
    },
    setBreadcrumb: (state, action: PayloadAction<ExplorerBreadcrumbItem[]>) => {
      state.breadcrumb = action.payload
      const activeFolder = action.payload[action.payload.length - 1]
      state.activeFolderId = activeFolder?.id ?? null
      state.activeFolderName = activeFolder?.name ?? null
      state.selectedItems = []
      state.lastSelectedItem = null
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload
      state.page = 1
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload
    },
    setDateFilter: (state, action: PayloadAction<FolderDateFilter>) => {
      state.dateFilter = action.payload
      state.page = 1
    },
    setSignedFilter: (state, action: PayloadAction<'all' | FolderSignedFilter>) => {
      state.signedFilter = action.payload
      state.page = 1
    },
    setViewMode: (state, action: PayloadAction<ExplorerViewMode>) => {
      state.viewMode = action.payload
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(EXPLORER_VIEW_MODE_STORAGE_KEY, action.payload)
      }
    },
    toggleSelectedItem: (state, action: PayloadAction<ExplorerSelectionItem>) => {
      const item = action.payload
      const alreadySelected = isExplorerItemSelected(state.selectedItems, item)
      state.selectedItems = alreadySelected
        ? state.selectedItems.filter((selected) => selected.type !== item.type || selected.id !== item.id)
        : [...state.selectedItems, item]
      state.lastSelectedItem = alreadySelected ? null : item
    },
    selectItem: (state, action: PayloadAction<ExplorerSelectionItem>) => {
      const item = action.payload
      if (!isExplorerItemSelected(state.selectedItems, item)) {
        state.selectedItems.push(item)
      }
      state.lastSelectedItem = item
    },
    unselectItem: (state, action: PayloadAction<ExplorerSelectionItem>) => {
      const item = action.payload
      state.selectedItems = state.selectedItems.filter(
        (selected) => selected.type !== item.type || selected.id !== item.id
      )
      state.lastSelectedItem = null
    },
    clearSelection: (state) => {
      state.selectedItems = []
      state.lastSelectedItem = null
    },
    setSelectedItems: (state, action: PayloadAction<ExplorerSelectionItem[]>) => {
      state.selectedItems = action.payload
      state.lastSelectedItem = action.payload[action.payload.length - 1] ?? null
    },
    resetExplorer: (state) => ({
      ...initialState,
      viewMode: state.viewMode || getStoredExplorerViewMode(),
    }),
  },
})

export const {
  setActiveTab,
  openFolder,
  goToFolder,
  goBackToRoot,
  setBreadcrumb,
  setSearchText,
  setPage,
  setDateFilter,
  setSignedFilter,
  setViewMode,
  toggleSelectedItem,
  selectItem,
  unselectItem,
  clearSelection,
  setSelectedItems,
  resetExplorer,
} = documentExplorerSlice.actions

export default documentExplorerSlice.reducer
